import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  collection, 
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { MenuCategory, MenuItem, CartItem } from '../types';
import { toast } from 'react-hot-toast';

interface MenuContextType {
  categories: MenuCategory[];
  menuItems: MenuItem[];
  cart: CartItem[];
  loading: boolean;
  addToCart: (item: MenuItem, quantity: number, selectedExtras: { extra: any; quantity: number }[], notes?: string) => void;
  removeFromCart: (itemId: string) => void;
  updateCartItem: (itemId: string, quantity: number, selectedExtras: { extra: any; quantity: number }[], notes?: string) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [cartInitialized, setCartInitialized] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('kiss-coffee-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCart(parsedCart);
        console.log('Cart loaded from localStorage:', parsedCart);
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
    setCartInitialized(true);
  }, []);

  // Save cart to localStorage whenever cart changes (but only after initialization)
  useEffect(() => {
    if (cartInitialized) {
      localStorage.setItem('kiss-coffee-cart', JSON.stringify(cart));
      console.log('Cart saved to localStorage:', cart);
    }
  }, [cart, cartInitialized]);

  // Remove unavailable items from cart when menu items or categories update
  useEffect(() => {
    if (cartInitialized && cart.length > 0 && menuItems.length > 0 && categories.length > 0) {
      const updatedCart = cart.filter(cartItem => {
        const menuItem = menuItems.find(item => item.id === cartItem.menuItem.id);
        
        // Check if item exists and is available
        if (!menuItem || !menuItem.isAvailable) {
          console.log(`Removing unavailable item from cart: ${cartItem.menuItem.name}`);
          toast.error(`${cartItem.menuItem.name} is no longer available and was removed from cart`, {
            duration: 4000,
          });
          return false;
        }
        
        // Check if item's category is active
        const category = categories.find(cat => cat.id === menuItem.categoryId);
        if (!category || !category.isActive) {
          console.log(`Removing item from inactive category: ${cartItem.menuItem.name}`);
          toast.error(`${cartItem.menuItem.name} was removed from cart (category unavailable)`, {
            duration: 4000,
          });
          return false;
        }
        
        return true;
      });

      // Only update cart if items were removed
      if (updatedCart.length !== cart.length) {
        setCart(updatedCart);
      }
    }
  }, [menuItems, categories, cartInitialized]); // Don't include cart in dependencies to avoid infinite loop

  useEffect(() => {
    console.log('Starting to load menu data...');
    
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('Loading timeout reached, setting loading to false');
      setLoading(false);
    }, 10000); // 10 second timeout
    
    // Try simple queries first without complex filters
    const categoriesQuery = query(collection(db, 'menuCategories'), orderBy('order', 'asc'));
    const menuItemsQuery = collection(db, 'menuItems');

    const unsubscribeCategories = onSnapshot(
      categoriesQuery, 
      (snapshot) => {
        console.log('Categories snapshot received:', snapshot.docs.length);
        const categoriesData = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Category data:', data);
          return {
            id: doc.id,
            ...data
          } as MenuCategory;
        });
        setCategories(categoriesData);
        console.log('Categories loaded:', categoriesData.length);
      },
      (error) => {
        console.error('Error loading categories:', error);
        setLoading(false);
      }
    );

    const unsubscribeMenuItems = onSnapshot(
      menuItemsQuery, 
      (snapshot) => {
        console.log('Menu items snapshot received:', snapshot.docs.length);
        const menuItemsData = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Menu item data:', data);
          return {
            id: doc.id,
            ...data
          } as MenuItem;
        });
        setMenuItems(menuItemsData);
        setLoading(false);
        console.log('Menu items loaded:', menuItemsData.length);
      },
      (error) => {
        console.error('Error loading menu items:', error);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeout);
      unsubscribeCategories();
      unsubscribeMenuItems();
    };
  }, []);

  const addToCart = (item: MenuItem, quantity: number, selectedExtras: { extra: any; quantity: number }[], notes?: string) => {
    // Prevent adding unavailable items to cart
    if (!item.isAvailable) {
      toast.error('This item is currently unavailable');
      return;
    }
    
    console.log('Adding to cart:', { item: item.name, quantity, selectedExtras, notes });
    console.log('Current cart before adding:', cart);
    
    const existingItemIndex = cart.findIndex(cartItem => 
      cartItem.menuItem.id === item.id &&
      JSON.stringify(cartItem.selectedExtras) === JSON.stringify(selectedExtras) &&
      cartItem.notes === notes
    );

    if (existingItemIndex > -1) {
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += quantity;
      setCart(updatedCart);
      console.log('Updated existing item in cart:', updatedCart);
      toast.success(`${item.name} quantity updated in cart!`);
    } else {
      const newCartItem: CartItem = {
        id: `${item.id}-${Date.now()}`,
        menuItem: item,
        quantity,
        selectedExtras,
        notes
      };
      const newCart = [...cart, newCartItem];
      setCart(newCart);
      console.log('Added new item to cart:', newCart);
      toast.success(`${item.name} added to cart!`);
    }
  };

  const removeFromCart = (itemId: string) => {
    const itemToRemove = cart.find(item => item.id === itemId);
    setCart(cart.filter(item => item.id !== itemId));
    if (itemToRemove) {
      toast.success(`${itemToRemove.menuItem.name} removed from cart!`);
    }
  };

  const updateCartItem = (itemId: string, quantity: number, selectedExtras: { extra: any; quantity: number }[], notes?: string) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    const updatedCart = cart.map(item => 
      item.id === itemId 
        ? { ...item, quantity, selectedExtras, notes }
        : item
    );
    setCart(updatedCart);
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('kiss-coffee-cart');
    toast.success('Cart cleared!');
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const itemTotal = item.menuItem.price * item.quantity;
      const extrasTotal = item.selectedExtras.reduce((extrasSum, extra) => 
        extrasSum + (extra.extra.price * extra.quantity), 0
      );
      return total + (itemTotal + extrasTotal);
    }, 0);
  };

  const value = {
    categories,
    menuItems,
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    getCartTotal,
    getCartItemCount
  };

  return (
    <MenuContext.Provider value={value}>
      {children}
    </MenuContext.Provider>
  );
};
